import { t } from "@/i18n";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Coins, Download, FolderPlus, Paintbrush, Printer, ShieldCheck, Sparkles, Wand2, ImageIcon } from "lucide-react";
import { api, formatApiError, pageImageUrl, downloadFile, openPrint, slug } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { GenerationSteps } from "@/components/GenerationSteps";
import { FactsPanel } from "@/components/FactsPanel";

const AgePicker = ({ groups, value, onChange }) => (
  <div className="grid grid-cols-3 gap-2" data-testid="age-group-picker">
    {groups.map((g) => (
      <button key={g.id} type="button" data-testid={`age-group-${g.id}`} onClick={() => onChange(g.id)}
        className={`rounded-2xl border-2 p-3 text-left transition-colors ${value === g.id ? "border-[#1E293B] bg-[#4ECDC4]" : "border-slate-200 bg-white hover:border-slate-300"}`}>
        <span className="block font-bold">{g.label}</span>
        <span className="block text-xs text-slate-600">{g.hint}</span>
      </button>
    ))}
  </div>
);

const ResultActions = ({ page }) => {
  const nav = useNavigate();
  return (
    <div className="flex flex-wrap gap-3">
      <button className="btn-pop btn-teal" data-testid="colour-it-button" onClick={() => nav(`/colour/${page.id}`)}><Paintbrush className="w-5 h-5" /> {t("colour_it_now")}</button>
      <button className="btn-pop btn-white" data-testid="download-pdf-button" onClick={() => downloadFile(`/pages/${page.id}/pdf`, `${slug(page.title)}.pdf`).catch(() => toast.error("Download failed"))}><Download className="w-5 h-5" /> PDF</button>
      <button className="btn-pop btn-white" data-testid="print-page-button" onClick={() => openPrint(`/pages/${page.id}/pdf`).catch(() => toast.error("Couldn't open print view"))}><Printer className="w-5 h-5" /> {t("print")}</button>
      <button className="btn-pop btn-white" data-testid="add-to-packet-button" onClick={() => nav("/packets", { state: { preselect: page.id } })}><FolderPlus className="w-5 h-5" /> {t("packet")}</button>
    </div>
  );
};

export default function CreatePage() {
  const { user, setCredits } = useAuth();
  const [catalog, setCatalog] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [age, setAge] = useState("6-8");
  const [category, setCategory] = useState("animals");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    api.get("/catalog").then((r) => setCatalog(r.data));
    api.get("/pages").then((r) => setRecent(r.data.slice(0, 6))).catch(() => {});
  }, []);

  const suggestions = catalog?.categories.find((c) => c.id === category)?.prompts[age] || [];

  const generate = async () => {
    if (prompt.trim().length < 2) return setError(t("type_an_idea_or_tap_a_suggestion_first"));
    setBusy(true);
    setError("");
    setResult(null);
    try {
      const { data } = await api.post("/pages/generate", { prompt, age_group: age, category }, { timeout: 180000 });
      setResult(data.page);
      setCredits(data.credits);
      setRecent((r) => [data.page, ...r].slice(0, 6));
      toast.success(t("page_generated_and_safety_approved"));
    } catch (e) {
      const msg = formatApiError(e);
      setError(msg);
      if (e.response?.status === 402) toast.error(msg, { action: { label: t("get_credits"), onClick: () => (window.location.href = "/credits") } });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10" data-testid="create-page">
      <div className="mb-8 rise">
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight">{t("what_shall_we_draw", { name: (user.name || "").split(" ").filter((w) => !/^(mr|mrs|ms|miss|mx|dr)\.?$/i.test(w))[0] || "" })}</h1>
        <p className="text-slate-600 mt-2 text-base sm:text-lg">{t("every_idea_is_safety_checked_and_every_p")}</p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <section className="lg:col-span-5 bg-white p-6 sm:p-8 rounded-3xl border-2 border-slate-100 shadow-lg space-y-6 rise d1">
          <div>
            <label className="text-sm font-bold text-slate-700" htmlFor="prompt">{t("your_idea")}</label>
            <textarea id="prompt" data-testid="prompt-input" rows={3} maxLength={200} value={prompt} onChange={(e) => setPrompt(e.target.value)}
              placeholder={t("e_g_a_turtle_swimming_with_fish")}
              className="mt-1.5 w-full rounded-2xl border-2 border-slate-200 px-4 py-3.5 text-lg outline-none focus:border-[#FF5252] resize-none transition-colors" />
          </div>
          <div>
            <p className="text-sm font-bold text-slate-700 mb-2">{t("age_group")}</p>
            {catalog && <AgePicker groups={catalog.age_groups} value={age} onChange={setAge} />}
          </div>
          <div>
            <p className="text-sm font-bold text-slate-700 mb-2">{t("need_ideas")}</p>
            <div className="flex flex-wrap gap-2 mb-3" data-testid="category-filter">
              {catalog?.categories.map((c) => (
                <button key={c.id} data-testid={`category-${c.id}`} onClick={() => setCategory(c.id)}
                  className={`chip ${category === c.id ? "bg-[#1E293B] text-white border-[#1E293B]" : "bg-white border-slate-200 text-slate-700"}`}>{c.label}</button>
              ))}
            </div>
            <div className="flex flex-wrap gap-2" data-testid="suggested-prompts">
              {suggestions.map((s, i) => (
                <button key={s} data-testid={`suggested-prompt-${i}`} onClick={() => setPrompt(s)}
                  className="chip bg-amber-50 border-amber-200 text-amber-900 hover:bg-amber-100 font-semibold">{s}</button>
              ))}
            </div>
          </div>
          {error && <p className="text-sm font-bold text-red-700 bg-red-50 border-2 border-red-100 rounded-2xl px-4 py-3" data-testid="generate-error">{error}</p>}
          <button className="btn-pop btn-red w-full text-lg" disabled={busy} onClick={generate} data-testid="generate-page-button">
            <Wand2 className="w-5 h-5" /> {busy ? t("creating") : t("create_colouring_page")}
            <span className="ml-1 inline-flex items-center gap-1 bg-white/25 rounded-full px-2.5 py-0.5 text-sm"><Coins className="w-3.5 h-3.5" />{catalog?.generation_cost ?? 1}</span>
          </button>
          {user.credits < 1 && <p className="text-sm text-center">{t("out_of_credits")} <Link to="/credits" className="font-bold text-[#FF5252]" data-testid="get-credits-link">{t("get_a_credit_pack")}</Link></p>}
        </section>

        <section className="lg:col-span-7 bg-amber-50/60 p-6 sm:p-8 rounded-3xl border-2 border-amber-200/60 shadow-inner min-h-[500px] flex flex-col rise d2" data-testid="preview-panel">
          {busy ? (
            <div className="flex-1 flex flex-col items-center justify-center gap-6">
              <h3 className="font-display text-2xl font-semibold">{t("sharpening_the_pencils")}</h3>
              <GenerationSteps />
            </div>
          ) : result ? (
            <div className="space-y-6" data-testid="generation-result">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h2 className="text-2xl sm:text-3xl font-bold" data-testid="result-title">{result.title}</h2>
                <span className="safety-badge" data-testid="safety-approved-badge"><ShieldCheck className="w-3.5 h-3.5" /> {t("safety_approved")}</span>
              </div>
              <div className="bg-white rounded-2xl border-2 border-slate-200 p-3">
                <img src={pageImageUrl(result.id)} alt={result.title} className="w-full max-h-[560px] object-contain" data-testid="result-image" />
              </div>
              <ResultActions page={result} />
              <FactsPanel page={result} />
            </div>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-center gap-4 text-slate-500">
              <div className="w-24 h-24 rounded-3xl border-4 border-dashed border-amber-300 flex items-center justify-center"><ImageIcon className="w-10 h-10 text-amber-400" /></div>
              <p className="font-display text-xl font-semibold text-slate-700">{t("your_page_will_appear_here")}</p>
              <p className="max-w-sm">{t("pick_an_age_group_choose_a_suggestion_or")}</p>
            </div>
          )}
        </section>
      </div>

      {recent.length > 0 && (
        <section className="mt-14" data-testid="recent-pages">
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-display text-2xl font-semibold">{t("recent_pages")}</h3>
            <Link to="/library" className="font-bold text-[#FF5252] hover:underline" data-testid="view-library-link">{t("see_all")}</Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {recent.map((p) => (
              <button key={p.id} onClick={() => setResult(p)} data-testid={`recent-page-${p.id}`} className="card-soft p-2 hover:border-[#4ECDC4] text-left">
                <img src={pageImageUrl(p.id)} alt={p.title} className="w-full aspect-square object-contain rounded-2xl bg-white" loading="lazy" />
                <p className="text-sm font-bold px-2 py-2 truncate">{p.title}</p>
              </button>
            ))}
          </div>
        </section>
      )}
      <div className="hidden"><Sparkles /></div>
    </div>
  );
}
